/**
 * Кнопки в уведомлениях о звонке (Android) — что значит нажатие и куда оно ведёт.
 *
 * <p>Уведомление о входящем было без «Ответить / Отклонить»: чтобы просто отказаться, надо было
 * открыть приложение, дождаться экрана звонка и нажать там. А после отбоя звонящего от звонка не
 * оставалось ничего — ни «пропущенного», ни способа перезвонить (аудит удобства 2026-10-04, §3
 * #29). Здесь только решение, без побочных эффектов: нажатия приходят из трёх мест (открытое
 * приложение, фон, холодный старт), и правило должно быть одним и проверяться тестом.
 * Исполняет решение {@code callNotificationActionRunner}.
 */
import type { IncomingCallUIPayload } from '../calls/incomingCallUiBridge';

/** Идентификаторы кнопок уведомлений. Строки уходят в notifee и возвращаются в событии. */
export const CALL_ACTION_ANSWER = 'call-answer';
export const CALL_ACTION_DECLINE = 'call-decline';
export const CALL_ACTION_CALL_BACK = 'call-back';
export const CALL_ACTION_MESSAGE = 'call-message';

/** `data.type` уведомлений звонка. Входящий — тот же `call`, что приходит с сервера. */
export const CALL_NOTIFICATION_TYPE_INCOMING = 'call';
export const CALL_NOTIFICATION_TYPE_MISSED = 'missed_call';

/** Что нажали: тело уведомления или кнопку на нём. */
export type CallNotificationPress = {
  kind: 'press' | 'action';
  actionId?: string | null;
  data?: Record<string, unknown> | null;
};

export type OutgoingCallParams = {
  chatId: string;
  title: string;
  incoming: false;
  callMode: 'audio' | 'video';
  callScope: 'direct';
  remoteUserId?: string;
  remoteHasAvatar?: boolean;
  remoteAvatarRevision?: number;
  remoteAvatarShape?: string | null;
};

export type CallNotificationAction =
  | { kind: 'answer'; payload: IncomingCallUIPayload }
  | { kind: 'decline'; chatId: string; inviteId: string | null }
  /** Тело уведомления о входящем — снова показать экран входящего. */
  | { kind: 'open_overlay'; payload: IncomingCallUIPayload }
  | { kind: 'call_back'; params: OutgoingCallParams }
  | { kind: 'message'; chatId: string; title: string }
  | { kind: 'ignore' };

function str(v: unknown): string {
  return typeof v === 'string' ? v.trim() : '';
}

/**
 * Входящий из `data` уведомления. Поля те же, что в пуше сервера и в запасном уведомлении
 * `fcmBackground`: `callerName`, `callMode`, `callScope`, `inviteId`, `title` (название группы),
 * `callerId` и аватар.
 */
export function incomingPayloadFromNotificationData(
  data: Record<string, unknown>,
): IncomingCallUIPayload | null {
  const chatId = str(data.chatId);
  if (!chatId) return null;
  const callScope = data.callScope === 'group' ? 'group' : 'direct';
  const callerName = str(data.callerName) || str(data.title) || 'Call';
  const inviteId = str(data.inviteId);
  const callerUserId = str(data.callerId);
  const groupTitle = callScope === 'group' ? str(data.title) : '';
  const revRaw = Number(data.callerAvatarRevision);
  const shape = str(data.callerAvatarShape);
  return {
    chatId,
    callerName,
    callMode: data.callMode === 'video' ? 'video' : 'audio',
    callScope,
    ...(inviteId ? { inviteId } : {}),
    ...(callerUserId
      ? {
          callerUserId,
          callerHasAvatar: data.callerHasAvatar === '1' || data.callerHasAvatar === 'true' || data.callerHasAvatar === true,
          callerAvatarRevision: Number.isFinite(revRaw) ? revRaw : 0,
          callerAvatarShape: shape || null,
        }
      : {}),
    ...(groupTitle ? { groupTitle } : {}),
  };
}

/**
 * Параметры «Перезвонить». Только личный звонок: «перезвонить» в группу значило бы обзвонить
 * всех её участников одним нажатием в шторке — в группе остаётся «Написать».
 */
export function callBackParamsFromNotificationData(data: Record<string, unknown>): OutgoingCallParams | null {
  const chatId = str(data.chatId);
  if (!chatId || data.callScope === 'group') return null;
  const callerUserId = str(data.callerId);
  const revRaw = Number(data.callerAvatarRevision);
  const shape = str(data.callerAvatarShape);
  return {
    chatId,
    title: str(data.callerName) || str(data.title) || 'Call',
    incoming: false,
    callMode: data.callMode === 'video' ? 'video' : 'audio',
    callScope: 'direct',
    ...(callerUserId
      ? {
          remoteUserId: callerUserId,
          remoteHasAvatar: data.callerHasAvatar === '1' || data.callerHasAvatar === 'true' || data.callerHasAvatar === true,
          remoteAvatarRevision: Number.isFinite(revRaw) ? revRaw : 0,
          remoteAvatarShape: shape || null,
        }
      : {}),
  };
}

/** Что сделать по нажатию в уведомлении о звонке. Всё, что к звонкам не относится, — `ignore`. */
export function decideCallNotificationAction(press: CallNotificationPress): CallNotificationAction {
  const data = press.data ?? {};
  const type = str(data.type);
  if (type === CALL_NOTIFICATION_TYPE_INCOMING) {
    const payload = incomingPayloadFromNotificationData(data);
    if (!payload) return { kind: 'ignore' };
    if (press.kind === 'press') return { kind: 'open_overlay', payload };
    if (press.actionId === CALL_ACTION_ANSWER) return { kind: 'answer', payload };
    if (press.actionId === CALL_ACTION_DECLINE) {
      return { kind: 'decline', chatId: payload.chatId, inviteId: payload.inviteId ?? null };
    }
    return { kind: 'ignore' };
  }
  if (type === CALL_NOTIFICATION_TYPE_MISSED && press.kind === 'action') {
    const chatId = str(data.chatId);
    if (!chatId) return { kind: 'ignore' };
    if (press.actionId === CALL_ACTION_CALL_BACK) {
      const params = callBackParamsFromNotificationData(data);
      return params ? { kind: 'call_back', params } : { kind: 'ignore' };
    }
    if (press.actionId === CALL_ACTION_MESSAGE) {
      return { kind: 'message', chatId, title: str(data.title) || str(data.callerName) || 'Chat' };
    }
  }
  // Тело уведомления о пропущенном ведёт в чат обычным путём (`navigateFromPushData` в App.tsx).
  return { kind: 'ignore' };
}
